import { LeftOutlined } from '@ant-design/icons'
import { Button, Input, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import FoodSafetyCorrectiveActionPanel from 'src/components/food-safety/FoodSafetyCorrectiveActionPanel'
import FoodSafetyDayNav from 'src/components/food-safety/FoodSafetyDayNav'
import {
  FoodSafetyExceptionFollowUp,
  FoodSafetyExceptionStatus,
  NeedsActionBadge,
} from 'src/components/food-safety/FoodSafetyExceptionFollowUp'
import FoodSafetyOutOfServiceDialog from 'src/components/food-safety/FoodSafetyOutOfServiceDialog'
import FoodSafetyUnresolvedStrip from 'src/components/food-safety/FoodSafetyUnresolvedStrip'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySubmitErrorBox,
  FoodSafetySubmitErrorLead,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import FadeIn from 'src/components/styled/FadeIn'
import { ollieFabPageClearanceCss } from 'src/components/ollie/ollieFabPlacement'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  FOOD_SAFETY_HISTORY_LOCKED_CODE,
  FOOD_SAFETY_REPORTS_PAYWALL_REASON,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SECTION_LABELS,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import useFoodSafetyExceptionFollowUps from 'src/hooks/useFoodSafetyExceptionFollowUps'
import useFoodSafetyLinkedCheck from 'src/hooks/useFoodSafetyLinkedCheck'
import useFoodSafetyOutOfService from 'src/hooks/useFoodSafetyOutOfService'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import useFoodSafetySubmissionKeys from 'src/hooks/useFoodSafetySubmissionKeys'
import useIsBlocked, { useCanAccess } from 'src/hooks/useIsBlocked'
import useFoodSafetyLatestRequest from 'src/hooks/useFoodSafetyLatestRequest'
import useFoodSafetyNewDayReload from 'src/hooks/useFoodSafetyNewDayReload'
import FoodSafetyCorrectReading from 'src/components/food-safety/FoodSafetyCorrectReading'
import FoodSafetyReportsPaywallModal from 'src/screens/food-safety/FoodSafetyReportsPaywallModal'
import {
  correctFoodSafetyLog,
  createFoodSafetyLog,
  fetchFoodSafetyDay,
  recordFoodSafetyExceptionRecheck,
} from 'src/utils/foodSafetyApi'
import {
  FOOD_SAFETY_NEEDS_ACTION_PARAM,
  countFoodSafetyOpenExceptions,
  formatFoodSafetyNeedsActionBadge,
  EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT,
  buildFoodSafetyCorrectiveActionBody,
  focusFoodSafetyCorrectiveField,
  pickFoodSafetyLogException,
  resolveFoodSafetyCorrectiveFocusField,
  resolveFoodSafetyRecheckValue,
  validateFoodSafetyCorrectiveDraft,
} from 'src/utils/foodSafetyCorrectiveAction'
import { trackFoodSafetyLogSubmitted } from 'src/utils/foodSafetyAnalytics'
import {
  FOOD_SAFETY_SUBMIT_ERROR_LEAD,
  resolveFoodSafetySubmitAffordance,
  formatFoodSafetySubmitError,
} from 'src/utils/foodSafetyFeedback'
import {
  buildFridgeTempLogBody,
  canAddFridgeTempLog,
  findFramedNextDefinitionId,
  findNextOpenFridgeDefinitionId,
  findNextOpenFridgeDefinitionIdAfter,
  formatFridgeDueBy,
  formatFridgeLogTime,
  formatFridgeLoggedBadge,
  formatFridgeOpenLeftLabel,
  formatFridgeDayProgressLabel,
  formatFridgeUnitType,
  formatLoggedTemp,
  fridgeRangeHint,
  isFridgeTempOutOfRange,
  resolveFridgeLogOutOfRange,
  parseFridgeTempInput,
  summarizeFridgeDayProgress,
  toggleFridgeTempSign,
  listFridgeReadings,
  FOOD_SAFETY_FRIDGE_ADD_READING_LABEL,
  formatFridgeFiveReadingsCopy,
  formatFridgeReadingPosition,
  formatFridgeReadingsLimitHint,
  fridgeDraftKey,
  fridgeDueSlotsFilled,
  formatFridgeUnfinishedSlotsCue,
  listFridgeDueSlots,
  listFridgeExtraLogs,
  nextUnfinishedFridgeSlotDueTime,
  FOOD_SAFETY_FRIDGE_READING_PERIOD,
} from 'src/utils/foodSafetyFridges'
import {
  buildFoodSafetyBrowseWindow,
  isFoodSafetyViewingToday,
  resolveFoodSafetyScheduleBadge,
  foodSafetyHomeHref,
  formatFoodSafetyOverviewBackLabel,
} from 'src/utils/foodSafetyHome'
import {
  canMarkFoodSafetyOutOfService,
  formatFoodSafetyBackInService,
  formatFoodSafetyOutOfServiceSince,
  isFridgeItemOutOfServiceNow,
  isFridgeItemPausedOnDay,
} from 'src/utils/foodSafetyOutOfService'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'
import { setupAddButtonLabel } from 'src/utils/foodSafetySetup'
import {
  foodSafetyTempToneFromOutOfRange,
  formatFoodSafetyCorrectiveNote,
  formatFoodSafetyTempParts,
} from 'src/utils/foodSafetyTempColour'
import {
  readFoodSafetyLinkedCheck,
  resolveFoodSafetyLinkedCheckLine,
} from 'src/utils/ollieActionDeepLinks'

const SECTION = FOOD_SAFETY_SECTION.FRIDGES_FREEZERS

const fridgeCardTestId = id => `fridge-card-${id}`

/**
 * Focus after Mark out of service / Return to service, when the button that
 * opened the dialog is gone: the unit's new control, else the card itself.
 */
export const findFridgeOutOfServiceFocus = definition => {
  const id = definition?._id ? String(definition._id) : null
  if (!id || typeof document === 'undefined') return null
  const card = document.querySelector(`[data-testid="fridge-card-${id}"]`)
  if (!card) return null
  const temp = card.querySelector(`[data-testid^="fridge-temp-${id}"]`)
  return (
    card.querySelector(`[data-testid="fridge-return-to-service-${id}"]`) ||
    (temp && (temp.tagName === 'INPUT' ? temp : temp.querySelector('input'))) ||
    card.querySelector(`[data-testid="fridge-add-another-${id}"]`) ||
    card.querySelector(`[data-testid="fridge-mark-out-of-service-${id}"]`) ||
    card
  )
}

const FoodSafetyFridgesScreen = () => {
  const blocked = useIsBlocked('food-safety')
  const canManage = useCanAccess('food-safety-manage')
  const history = useHistory()
  const location = useLocation()
  const { venueId: routeVenueId } = useParams()
  const venue = useSelector(state => state.venue?.venue)
  const venueId = venue?._id || routeVenueId

  const queryDate = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('date') || undefined
  }, [location.search])
  const jumpToNeedsAction = useMemo(
    () =>
      new URLSearchParams(location.search).get(
        FOOD_SAFETY_NEEDS_ACTION_PARAM,
      ) === '1',
    [location.search],
  )
  const jumpToNeedsActionRef = useRef(jumpToNeedsAction)
  jumpToNeedsActionRef.current = jumpToNeedsAction
  const landedFromOllieRef = useRef(false)
  landedFromOllieRef.current = Boolean(
    readFoodSafetyLinkedCheck(location.search),
  )

  const [day, setDay] = useState(null)
  const newDayKey = useFoodSafetyNewDayReload(day)
  const [loading, setLoading] = useState(true)
  const [drafts, setDrafts] = useState({}) // definitionId → { value, valueText, action, actionNudged }
  const [submitErrors, setSubmitErrors] = useState({})
  const [submittingId, setSubmittingId] = useState(null)
  const [addingId, setAddingId] = useState(null)
  const [historyPaywallOpen, setHistoryPaywallOpen] = useState(false)
  const [lockAccess, setLockAccess] = useState(null)
  const latestDay = useFoodSafetyLatestRequest()
  const saveLock = useFoodSafetySaveLock()
  const submissionKeys = useFoodSafetySubmissionKeys()

  const focusDefinition = useCallback((definitionId, period) => {
    if (!definitionId) return
    const suffix = period ? `-${period}` : ''
    requestAnimationFrame(() => {
      const root = document.querySelector(
        `[data-testid="fridge-temp-${definitionId}${suffix}"]`,
      )
      const input = root?.querySelector?.('input') || root
      if (input?.scrollIntoView) {
        input.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
      if (input?.focus) input.focus()
      if (input?.select) input.select()
    })
  }, [])

  const focusCorrective = useCallback((definitionId, actionDraft) => {
    if (!definitionId) return
    focusFoodSafetyCorrectiveField(
      `fridge-corrective-${definitionId}`,
      resolveFoodSafetyCorrectiveFocusField(actionDraft),
    )
  }, [])

  const advanceToOpenFridge = useCallback(
    (fromDefinitionId, { reverse = false } = {}) => {
      const nextId = findNextOpenFridgeDefinitionIdAfter(
        day?.items || [],
        fromDefinitionId,
        { reverse },
      )
      if (nextId) focusDefinition(nextId)
    },
    [day?.items, focusDefinition],
  )

  const loadDay = useCallback(async () => {
    if (!venueId) return
    const isCurrent = latestDay.begin()
    setLoading(true)
    try {
      const data = await fetchFoodSafetyDay(venueId, {
        section: SECTION,
        date: queryDate,
      })
      if (!isCurrent()) return
      setDay(data)
      // 29.62 — past-day browse must not steal focus; today rounds still autofocus
      const viewingToday = isFoodSafetyViewingToday(
        data?.localDate,
        data?.todayLocalDate,
      )
      // The Home "needs action" chip lands on the open exception instead, and
      // Ollie's "Do the check" lands on the card it named.
      if (
        viewingToday &&
        !jumpToNeedsActionRef.current &&
        !landedFromOllieRef.current
      ) {
        const nextId =
          data.nextOpenDefinitionId ||
          findNextOpenFridgeDefinitionId(data.items || [])
        focusDefinition(nextId)
      }
    } catch (error) {
      if (!isCurrent()) return
      if (error?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE) {
        setLockAccess(error.historyAccess || null)
        setHistoryPaywallOpen(true)
        message.warning(error.message)
        // Stay on this section so the paywall remains visible (29.16).
        // Clamp to the Free window edge when we can — never bounce to Home.
        const earliest = error.historyAccess?.window?.earliestLocalDate
        if (earliest && earliest !== queryDate) {
          history.replace(
            `/v/${venueId}/food-safety/section/${SECTION}?date=${earliest}`,
          )
        }
        return
      }
      console.error(error)
      message.error(error?.message || 'Could not load fridges for this day.')
    } finally {
      isCurrent.done()
      if (isCurrent()) setLoading(false)
    }
  }, [venueId, queryDate, focusDefinition, history, latestDay])

  useEffect(() => {
    loadDay()
  }, [loadDay, newDayKey])

  /** Reload in place; resolves to the new day, or null when a switch won. */
  const refreshDay = useCallback(async () => {
    if (!venueId || !day?.localDate) return null
    const isCurrent = latestDay.beginQuiet()
    if (isCurrent.skipped) return null
    const data = await fetchFoodSafetyDay(venueId, {
      section: SECTION,
      date: day.localDate,
    })
    if (!isCurrent()) return null
    setDay(data)
    return data
  }, [venueId, day?.localDate, latestDay])

  const followUps = useFoodSafetyExceptionFollowUps({
    venueId,
    onSaved: refreshDay,
  })
  const resetFollowUps = followUps.reset

  const outOfService = useFoodSafetyOutOfService({
    venueId,
    onChanged: refreshDay,
    fallbackFocus: findFridgeOutOfServiceFocus,
    canManage,
    timezone: day?.timezone,
  })

  // H9 / 29.25 — day switch must not submit yesterday’s typed temps
  useEffect(() => {
    setDrafts({})
    setSubmitErrors({})
    resetFollowUps()
  }, [queryDate, newDayKey, resetFollowUps])

  const browseWindow = useMemo(
    () =>
      buildFoodSafetyBrowseWindow(
        day?.historyAccess || lockAccess,
        day?.localDate,
      ),
    [day?.historyAccess, day?.localDate, lockAccess],
  )

  const goToSectionDate = nextDate => {
    if (!venueId || !nextDate) return
    history.push(
      `/v/${venueId}/food-safety/section/${SECTION}?date=${nextDate}`,
    )
  }

  const openHistoryPaywall = () => setHistoryPaywallOpen(true)

  const viewingToday = isFoodSafetyViewingToday(
    day?.localDate,
    day?.todayLocalDate,
  )

  const updateDraft = (definitionId, patch) => {
    setSubmitErrors(prev => {
      if (!prev[definitionId]) return prev
      const next = { ...prev }
      delete next[definitionId]
      return next
    })
    setDrafts(prev => ({
      ...prev,
      [definitionId]: { ...(prev[definitionId] || {}), ...patch },
    }))
  }

  const clearDraft = definitionId => {
    const drop = prev => {
      if (!prev[definitionId]) return prev
      const next = { ...prev }
      delete next[definitionId]
      return next
    }
    setDrafts(drop)
    setSubmitErrors(drop)
  }

  // Tab keeps the browser's order (temp → Submit → actions → next unit) so
  // keyboard users can reach Submit and the chips; Enter jumps between units.
  const handleTempKeyDown = (event, item, period) => {
    const definitionId = String(item.definition._id)
    const draftKey = fridgeDraftKey(definitionId, period)
    if (event.key !== 'Enter') return
    event.preventDefault()
    if (saveLock.isHeld(definitionId)) return
    const draft = drafts[draftKey] || {}
    const value = parseFridgeTempInput(
      draft.valueText != null ? draft.valueText : draft.value,
    )
    if (value == null) {
      const nextSlot = nextUnfinishedFridgeSlotDueTime(item, period)
      if (nextSlot && nextSlot !== period) {
        focusDefinition(definitionId, nextSlot)
        return
      }
      advanceToOpenFridge(definitionId)
      return
    }
    const config = item.definition.config || {}
    const outOfRange = isFridgeTempOutOfRange(value, config)
    if (outOfRange && !draft.action?.type && !draft.actionNudged) {
      updateDraft(draftKey, { actionNudged: true })
      focusCorrective(definitionId)
      return
    }
    submitTemp(item, period)
  }

  const updateActionDraft = (definitionId, patch) => {
    const current = drafts[definitionId]?.action || {}
    updateDraft(definitionId, {
      action: { ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT, ...current, ...patch },
    })
  }

  const submitTemp = async (item, period) => {
    const definitionId = String(item.definition._id)
    const draftKey = fridgeDraftKey(definitionId, period)
    const draft = drafts[draftKey] || {}
    const value = parseFridgeTempInput(
      draft.valueText != null ? draft.valueText : draft.value,
    )
    if (value == null) {
      message.warning('Enter a temperature first.')
      focusDefinition(definitionId, period)
      return
    }

    const config = item.definition.config || {}
    const outOfRange = isFridgeTempOutOfRange(value, config)
    const actionDraft = outOfRange ? draft.action || {} : {}
    const draftError = validateFoodSafetyCorrectiveDraft(actionDraft)
    if (draftError) {
      setSubmitErrors(prev => ({ ...prev, [draftKey]: draftError }))
      focusCorrective(definitionId, actionDraft)
      return
    }
    const correctiveAction = buildFoodSafetyCorrectiveActionBody(actionDraft)
    const recheckValue = viewingToday
      ? resolveFoodSafetyRecheckValue(actionDraft)
      : null

    if (!saveLock.tryAcquire(definitionId)) return
    setSubmittingId(definitionId)
    const saveSlot = period
      ? `${day.localDate}:${definitionId}:${period}`
      : `${day.localDate}:${definitionId}`
    try {
      let saved
      try {
        saved = await createFoodSafetyLog(
          venueId,
          buildFridgeTempLogBody({
            definitionId,
            localDate: day.localDate,
            value,
            dueTime:
              period && period !== FOOD_SAFETY_FRIDGE_READING_PERIOD.EXTRA
                ? period
                : undefined,
            readingPeriod:
              period === FOOD_SAFETY_FRIDGE_READING_PERIOD.EXTRA
                ? FOOD_SAFETY_FRIDGE_READING_PERIOD.EXTRA
                : undefined,
            correctiveAction,
          }),
          {
            submissionKey: submissionKeys.keyFor(saveSlot, {
              value,
              correctiveAction,
              dueTime: period,
            }),
          },
        )
      } catch (error) {
        // 29.37 — keep draft; Retry on the card.
        const msg = formatFoodSafetySubmitError(error)
        setSubmitErrors(prev => ({ ...prev, [draftKey]: msg }))
        message.error(msg)
        return
      }
      submissionKeys.clear(saveSlot)
      trackFoodSafetyLogSubmitted({
        venueId,
        section: SECTION,
        outOfRange: Boolean(outOfRange),
      })
      if (outOfRange && !correctiveAction) {
        message.warning(
          `${item.definition.name} saved — needs corrective action`,
        )
      } else {
        message.success(`${item.definition.name} logged`)
      }
      if (recheckValue != null && saved?.exception?._id) {
        try {
          await recordFoodSafetyExceptionRecheck(venueId, saved.exception._id, {
            value: recheckValue,
          })
        } catch (recheckError) {
          message.warning(
            `Reading saved, but the recheck didn’t save — ${formatFoodSafetySubmitError(
              recheckError,
            )}`,
          )
        }
      }
      clearDraft(draftKey)
      if (period === FOOD_SAFETY_FRIDGE_READING_PERIOD.EXTRA) {
        setAddingId(prev => (prev === definitionId ? null : prev))
      }
      let data = null
      const refreshed = await refreshAfterFoodSafetySave(async () => {
        data = await refreshDay()
      })
      if (!refreshed) {
        message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
        return
      }
      const sameItem = (data?.items || []).find(
        row => String(row.definition?._id) === definitionId,
      )
      const nextSlot = nextUnfinishedFridgeSlotDueTime(sameItem || item, period)
      if (nextSlot) {
        focusDefinition(definitionId, nextSlot)
        return
      }
      if (!data) return
      const nextId =
        data.nextOpenDefinitionId ||
        findNextOpenFridgeDefinitionId(data.items || [])
      if (nextId) {
        const nextItem = (data.items || []).find(
          row => String(row.definition?._id) === String(nextId),
        )
        focusDefinition(
          nextId,
          nextUnfinishedFridgeSlotDueTime(nextItem) || undefined,
        )
      }
    } finally {
      saveLock.release(definitionId)
      setSubmittingId(null)
    }
  }

  const saveFridgeCorrection = async (log, { value, note }) => {
    if (!venueId || !log?._id) return false
    const key = `correct-${log._id}`
    if (!saveLock.tryAcquire(key)) return false
    try {
      await correctFoodSafetyLog(venueId, String(log._id), { value, note })
      const refreshed = await refreshAfterFoodSafetySave(refreshDay)
      if (!refreshed) message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
      return true
    } catch (error) {
      message.error(formatFoodSafetySubmitError(error))
      return false
    } finally {
      saveLock.release(key)
    }
  }

  const linkedCheck = useFoodSafetyLinkedCheck({
    search: location.search,
    items: day?.items,
    cardTestId: fridgeCardTestId,
    itemsReady: !loading && Boolean(day),
    venueId,
    history,
    onLanded: id => {
      const dueTime = new URLSearchParams(location.search).get('dueTime')
      focusDefinition(id, dueTime || undefined)
    },
  })

  const items = day?.items || []
  const progress = summarizeFridgeDayProgress(items)
  const { openCount, dueTotal, waitingCount } = progress
  const progressLabel = formatFridgeDayProgressLabel(progress)
  const unfinishedCue = formatFridgeUnfinishedSlotsCue(items)
  const openLeftLabel =
    unfinishedCue || formatFridgeOpenLeftLabel(openCount, waitingCount)

  if (blocked) return <PermissionBlockScreen />
  if (!venueId || (loading && !day)) return <Loading />

  const overviewHref = foodSafetyHomeHref(venueId, {
    localDate: day?.localDate,
    todayLocalDate: day?.todayLocalDate,
  })
  const overviewLabel = formatFoodSafetyOverviewBackLabel(
    day?.localDate,
    day?.todayLocalDate,
  )

  return (
    <Wrapper>
      <TopBar>
        <BackLink to={overviewHref} data-testid='fs-section-back'>
          <LeftOutlined /> {overviewLabel}
        </BackLink>
      </TopBar>

      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>
              {FOOD_SAFETY_SECTION_LABELS[SECTION]}
            </FoodSafetyPageTitle>
            <BodyText>
              {progressLabel || 'Log fridge and freezer temperatures'}
            </BodyText>
          </div>
          {day?.localDate ? (
            <FoodSafetyDayNav
              localDate={day.localDate}
              browseWindow={browseWindow}
              onChangeDate={goToSectionDate}
              onBrowseLocked={openHistoryPaywall}
              disabled={loading}
            />
          ) : null}
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>

      {day?.localDate ? (
        <FoodSafetyUnresolvedStrip
          venueId={venueId}
          section={SECTION}
          viewedLocalDate={day.localDate}
          todayLocalDate={day.todayLocalDate}
          refreshKey={day}
          onResolved={refreshDay}
          autoJump={jumpToNeedsAction}
          testIdPrefix='fridge-unresolved'
        />
      ) : null}

      {items.length === 0 ? (
        <EmptyCard>
          <H3>No fridges or freezers yet</H3>
          <BodyText>
            {canManage
              ? 'Add your fridges in setup so the kitchen can log temps.'
              : 'Ask a manager to add fridge and freezer units in Food Safety setup.'}
          </BodyText>
          {canManage ? (
            <SetupLink
              to={`/v/${venueId}/food-safety/setup?section=fridges_freezers&add=1`}
            >
              {setupAddButtonLabel(SECTION)}
            </SetupLink>
          ) : null}
        </EmptyCard>
      ) : (
        <>
          {openLeftLabel ? (
            <OpenLeftSticky
              data-testid='fridge-open-left'
              data-quiet={linkedCheck ? 'true' : 'false'}
              $quiet={Boolean(linkedCheck)}
            >
              <OpenLeftLabel>{openLeftLabel}</OpenLeftLabel>
              <OpenLeftHint $quiet={Boolean(linkedCheck)}>
                Enter for next
              </OpenLeftHint>
            </OpenLeftSticky>
          ) : null}
          <FiveReadingsNote data-testid='fridge-five-readings'>
            {formatFridgeFiveReadingsCopy()}
          </FiveReadingsNote>
          <CardList>
            {items.map(item => {
              const def = item.definition
              const id = String(def._id)
              const draft = drafts[id] || {}
              const config = def.config || {}
              const unitLabel = formatFridgeUnitType(config.unitType)
              const range = fridgeRangeHint(config)
              const completed = Boolean(item.completed)
              const logs = listFridgeReadings(item)
              const canAdd = canAddFridgeTempLog(item)
              const dueSlots = listFridgeDueSlots(item)
              const extraLogs = listFridgeExtraLogs(item)
              const slotsFilled = fridgeDueSlotsFilled(item)
              const dueLabel =
                dueSlots.length <= 1 ? formatFridgeDueBy(def.dueByTime) : null
              const paused = isFridgeItemPausedOnDay(item, day.localDate)
              const outNow = isFridgeItemOutOfServiceNow(item)
              const showEntry =
                !paused && (!completed || (canAdd && addingId === id))
              const canMarkOut =
                viewingToday &&
                !outNow &&
                canMarkFoodSafetyOutOfService({
                  ...def,
                  section: def.section || SECTION,
                })
              const loggedBadge = formatFridgeLoggedBadge(item)
              const needsActionBadge = formatFoodSafetyNeedsActionBadge(
                countFoodSafetyOpenExceptions(logs),
              )
              const draftTemp = parseFridgeTempInput(
                draft.valueText != null ? draft.valueText : draft.value,
              )
              const outOfRangePreview = isFridgeTempOutOfRange(
                draftTemp,
                config,
              )
              const submitAffordance = resolveFoodSafetySubmitAffordance({
                outOfRange: outOfRangePreview,
                corrective: draft.action?.type || '',
                saveFailed: Boolean(submitErrors[id]),
                actionOptional: true,
              })
              const isNext =
                findFramedNextDefinitionId(items, day.nextOpenDefinitionId) ===
                id
              const scheduleBadge = resolveFoodSafetyScheduleBadge(item, {
                isNext: isNext && !completed,
              })
              const linkedLine = resolveFoodSafetyLinkedCheckLine(
                linkedCheck,
                item,
              )

              return (
                <UnitCard
                  key={id}
                  data-completed={completed ? 'true' : 'false'}
                  data-next={isNext && !completed ? 'true' : 'false'}
                  data-out-of-service={paused ? 'true' : 'false'}
                  data-testid={fridgeCardTestId(id)}
                  data-ollie-linked={
                    linkedCheck?.definitionId === id ? 'true' : 'false'
                  }
                  tabIndex={-1}
                >
                  <UnitHeader>
                    <div>
                      <UnitName>{def.name}</UnitName>
                      <MetaLine>
                        {[
                          unitLabel,
                          range,
                          !completed && !paused ? dueLabel : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </MetaLine>
                    </div>
                    {paused ? (
                      <OutOfServiceBadge
                        data-testid={`fridge-out-of-service-badge-${id}`}
                      >
                        Out of service
                      </OutOfServiceBadge>
                    ) : needsActionBadge ? (
                      <NeedsActionBadge
                        data-testid={`fridge-needs-action-badge-${id}`}
                      >
                        {needsActionBadge}
                      </NeedsActionBadge>
                    ) : loggedBadge ? (
                      <DoneBadge>{loggedBadge}</DoneBadge>
                    ) : scheduleBadge?.tone === 'missed' ? (
                      <MissedBadge>{scheduleBadge.label}</MissedBadge>
                    ) : scheduleBadge?.tone === 'quiet' && !linkedLine ? (
                      <DoneBadge>{scheduleBadge.label}</DoneBadge>
                    ) : scheduleBadge?.tone === 'open' ? (
                      <NextBadge>{scheduleBadge.label}</NextBadge>
                    ) : null}
                  </UnitHeader>
                  {linkedLine ? (
                    <LinkedMissedLine
                      data-testid={`fridge-linked-missed-${id}`}
                    >
                      {linkedLine}
                    </LinkedMissedLine>
                  ) : null}

                  {paused ? (
                    <OutOfServiceNote
                      role='status'
                      data-testid={`fridge-out-of-service-${id}`}
                    >
                      <OutOfServiceLine>
                        {formatFoodSafetyOutOfServiceSince(item.outOfService, {
                          todayLocalDate: day.todayLocalDate,
                        })}
                      </OutOfServiceLine>
                      <OutOfServiceHint>
                        No readings needed while it’s out of service.
                      </OutOfServiceHint>
                      {viewingToday && outNow ? (
                        <ReturnButton
                          onClick={() => outOfService.confirmReturn(def)}
                          data-testid={`fridge-return-to-service-${id}`}
                        >
                          Return to service
                        </ReturnButton>
                      ) : null}
                    </OutOfServiceNote>
                  ) : item.outOfService ? (
                    <OutOfServiceHint
                      data-testid={`fridge-back-in-service-${id}`}
                    >
                      {formatFoodSafetyBackInService(item.outOfService)}
                    </OutOfServiceHint>
                  ) : null}

                  {dueSlots.length > 0 && !paused ? (
                    <div data-testid={`fridge-due-slots-${id}`}>
                      {dueSlots.map(slot => {
                        const period = slot.dueTime
                        const slotLabel = formatFridgeDueBy(slot.dueTime)
                        const draftKey = fridgeDraftKey(id, period)
                        const slotDraft = drafts[draftKey] || {}
                        const slotTemp = parseFridgeTempInput(
                          slotDraft.valueText != null
                            ? slotDraft.valueText
                            : slotDraft.value,
                        )
                        const slotOut = isFridgeTempOutOfRange(slotTemp, config)
                        const slotAction =
                          slotDraft.action || EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT
                        const slotAffordance =
                          resolveFoodSafetySubmitAffordance({
                            outOfRange: slotOut,
                            corrective: slotAction.type,
                            actionOptional: true,
                            saveFailed: Boolean(submitErrors[draftKey]),
                          })
                        if (slot.log) {
                          const exception = pickFoodSafetyLogException(slot.log)
                          const tempParts = formatFoodSafetyTempParts({
                            value: slot.log.payload?.value,
                            outOfRange: resolveFridgeLogOutOfRange(
                              slot.log,
                              config,
                            ),
                          })
                          const time = formatFridgeLogTime(
                            slot.log?.completedAt,
                          )
                          return (
                            <LoggedRow
                              key={period}
                              data-testid={`fridge-slot-${period}-${id}`}
                            >
                              <SlotLabel>{slotLabel}</SlotLabel>
                              <LoggedMeta>
                                <LoggedTemp data-tone={tempParts.tone}>
                                  {tempParts.tempLabel ||
                                    formatLoggedTemp(slot.log)}
                                </LoggedTemp>
                                {time ? ` · ${time}` : ''}
                                {slot.log?.completedByName
                                  ? ` · ${slot.log.completedByName}`
                                  : ''}
                              </LoggedMeta>
                              <FoodSafetyExceptionStatus
                                exception={exception}
                                fallbackNote={formatFoodSafetyCorrectiveNote(
                                  slot.log.payload?.correctiveAction,
                                )}
                                testId={`fridge-needs-action-${id}-${period}`}
                              />
                              <FoodSafetyExceptionFollowUp
                                exception={exception}
                                section={SECTION}
                                fallbackReading={{
                                  value: slot.log.payload?.value,
                                  tempMin: config.tempMin,
                                  tempMax: config.tempMax,
                                }}
                                viewingToday={viewingToday}
                                followUps={followUps}
                                testIdPrefix='fridge'
                                rowKey={`${id}-${period}`}
                              />
                              <FoodSafetyCorrectReading
                                log={slot.log}
                                testId={`fridge-correct-${id}-${period}`}
                                disabled={submittingId === id}
                                onCorrect={fields =>
                                  saveFridgeCorrection(slot.log, fields)
                                }
                              />
                            </LoggedRow>
                          )
                        }
                        return (
                          <div
                            key={period}
                            data-testid={`fridge-slot-${period}-${id}`}
                          >
                            <SlotLabel>{slotLabel}</SlotLabel>
                            <EntryRow>
                              <TempField>
                                <SignButton
                                  type='button'
                                  onClick={() => {
                                    const current =
                                      slotDraft.valueText != null
                                        ? String(slotDraft.valueText)
                                        : ''
                                    const next = toggleFridgeTempSign(current)
                                    updateDraft(draftKey, {
                                      valueText: next,
                                      value: parseFridgeTempInput(next),
                                    })
                                  }}
                                  data-testid={`fridge-sign-${id}-${period}`}
                                  aria-label={`${def.name} ${slotLabel}: toggle negative temperature`}
                                >
                                  −
                                </SignButton>
                                <Input
                                  value={
                                    slotDraft.valueText != null
                                      ? String(slotDraft.valueText)
                                      : ''
                                  }
                                  onChange={e => {
                                    const valueText = e.target.value
                                    updateDraft(draftKey, {
                                      valueText,
                                      value: parseFridgeTempInput(valueText),
                                    })
                                  }}
                                  onKeyDown={e =>
                                    handleTempKeyDown(e, item, period)
                                  }
                                  placeholder='°C'
                                  aria-label={`${def.name} ${slotLabel} temperature (°C)`}
                                  data-testid={`fridge-temp-${id}-${period}`}
                                  style={{ width: '7rem' }}
                                />
                                <span>°C</span>
                              </TempField>
                              <Button
                                type={
                                  slotAffordance.quiet ? 'default' : 'primary'
                                }
                                loading={submittingId === id}
                                onClick={() => submitTemp(item, period)}
                                data-testid={`fridge-submit-${id}-${period}`}
                              >
                                {slotAffordance.label}
                              </Button>
                            </EntryRow>
                            {slotOut && slotTemp != null ? (
                              <FoodSafetyCorrectiveActionPanel
                                section={SECTION}
                                reading={{
                                  value: slotTemp,
                                  tempMin: config.tempMin,
                                  tempMax: config.tempMax,
                                }}
                                draft={slotAction}
                                onChange={patch =>
                                  updateActionDraft(draftKey, patch)
                                }
                                showRecheck={viewingToday}
                                onNoteSubmit={() => submitTemp(item, period)}
                                onSaveWithoutAction={() =>
                                  submitTemp(item, period)
                                }
                                testIdPrefix={`fridge-corrective-${id}-${period}`}
                              />
                            ) : null}
                          </div>
                        )
                      })}
                      {slotsFilled && canAdd && addingId !== id ? (
                        <Button
                          type='link'
                          onClick={() => setAddingId(id)}
                          data-testid={`fridge-add-another-${id}`}
                        >
                          + {FOOD_SAFETY_FRIDGE_ADD_READING_LABEL}
                        </Button>
                      ) : null}
                      {slotsFilled && canAdd && addingId === id ? (
                        <div data-testid={`fridge-slot-extra-${id}`}>
                          <SlotLabel>
                            {formatFridgeReadingPosition(item)}
                          </SlotLabel>
                          <EntryRow>
                            <Input
                              value={String(
                                (drafts[fridgeDraftKey(id, 'extra')] || {})
                                  .valueText || '',
                              )}
                              onChange={e =>
                                updateDraft(fridgeDraftKey(id, 'extra'), {
                                  valueText: e.target.value,
                                  value: parseFridgeTempInput(e.target.value),
                                })
                              }
                              onKeyDown={e =>
                                handleTempKeyDown(
                                  e,
                                  item,
                                  FOOD_SAFETY_FRIDGE_READING_PERIOD.EXTRA,
                                )
                              }
                              placeholder='°C'
                              aria-label={`${def.name} extra temperature (°C)`}
                              data-testid={`fridge-temp-${id}-extra`}
                              style={{ width: '7rem' }}
                            />
                            <Button
                              type='primary'
                              onClick={() =>
                                submitTemp(
                                  item,
                                  FOOD_SAFETY_FRIDGE_READING_PERIOD.EXTRA,
                                )
                              }
                              data-testid={`fridge-submit-${id}-extra`}
                            >
                              Save
                            </Button>
                            <Button
                              onClick={() => {
                                clearDraft(fridgeDraftKey(id, 'extra'))
                                setAddingId(null)
                              }}
                            >
                              Cancel
                            </Button>
                          </EntryRow>
                        </div>
                      ) : null}
                      {!canAdd && slotsFilled ? (
                        <MaxHint data-testid={`fridge-readings-limit-${id}`}>
                          {formatFridgeReadingsLimitHint()}
                        </MaxHint>
                      ) : null}
                    </div>
                  ) : null}

                  {extraLogs.length > 0 ? (
                    <LoggedList data-testid={`fridge-logs-${id}`}>
                      {extraLogs.map((log, logIndex) => {
                        const resolvedOutOfRange = resolveFridgeLogOutOfRange(
                          log,
                          config,
                        )
                        const tempParts = formatFoodSafetyTempParts({
                          value: log?.payload?.value,
                          outOfRange: resolvedOutOfRange,
                        })
                        const exception = pickFoodSafetyLogException(log)
                        const time = formatFridgeLogTime(log?.completedAt)
                        return (
                          <LoggedRow
                            key={String(log._id || log.completedAt)}
                            data-separated={logIndex > 0 ? 'true' : 'false'}
                            data-testid={`fridge-log-${id}-${logIndex}`}
                          >
                            <LoggedTemp data-tone={tempParts.tone}>
                              {tempParts.tempLabel || formatLoggedTemp(log)}
                            </LoggedTemp>
                            {tempParts.statusLabel ? (
                              <OutStatus data-tone={tempParts.tone}>
                                {tempParts.statusLabel}
                              </OutStatus>
                            ) : null}
                            <FoodSafetyExceptionStatus
                              exception={exception}
                              fallbackNote={formatFoodSafetyCorrectiveNote(
                                log?.payload?.correctiveAction,
                              )}
                              testId={`fridge-needs-action-${id}-${logIndex}`}
                            />
                            <LoggedMeta>
                              {log?.completedByName || 'Logged'}
                              {time ? ` · ${time}` : ''}
                            </LoggedMeta>
                            <FoodSafetyCorrectReading
                              log={log}
                              testId={`fridge-correct-${id}-${logIndex}`}
                              disabled={submittingId === id}
                              onCorrect={fields =>
                                saveFridgeCorrection(log, fields)
                              }
                            />
                            <FoodSafetyExceptionFollowUp
                              exception={exception}
                              section={SECTION}
                              fallbackReading={{
                                value: log?.payload?.value,
                                tempMin: config.tempMin,
                                tempMax: config.tempMax,
                              }}
                              viewingToday={viewingToday}
                              followUps={followUps}
                              testIdPrefix='fridge'
                              rowKey={`${id}-${logIndex}`}
                            />
                          </LoggedRow>
                        )
                      })}
                    </LoggedList>
                  ) : null}

                  {showEntry && dueSlots.length === 0 ? (
                    <>
                      {submitErrors[id] ? (
                        <FoodSafetySubmitErrorBox
                          data-testid={`fridge-submit-error-${id}`}
                          role='status'
                        >
                          <FoodSafetySubmitErrorLead>
                            {FOOD_SAFETY_SUBMIT_ERROR_LEAD}
                          </FoodSafetySubmitErrorLead>
                          {submitErrors[id]}
                        </FoodSafetySubmitErrorBox>
                      ) : null}
                      {completed ? (
                        <ReadingPosition
                          data-testid={`fridge-reading-position-${id}`}
                        >
                          {formatFridgeReadingPosition(item)}
                        </ReadingPosition>
                      ) : null}
                      <EntryRow>
                        <TempField
                          data-tone={
                            draftTemp == null
                              ? 'unknown'
                              : foodSafetyTempToneFromOutOfRange(
                                  outOfRangePreview,
                                )
                          }
                        >
                          <SignButton
                            type='button'
                            onClick={() => {
                              const current =
                                draft.valueText != null
                                  ? String(draft.valueText)
                                  : draft.value != null && draft.value !== ''
                                  ? String(draft.value)
                                  : ''
                              const next = toggleFridgeTempSign(current)
                              updateDraft(id, {
                                valueText: next,
                                value: parseFridgeTempInput(next),
                                actionNudged: false,
                              })
                              focusDefinition(id)
                            }}
                            data-testid={`fridge-sign-${id}`}
                            aria-label={`${item.definition.name}: toggle negative temperature`}
                            title='Negative (°C)'
                          >
                            −
                          </SignButton>
                          <Input
                            value={
                              draft.valueText != null
                                ? String(draft.valueText)
                                : draft.value != null && draft.value !== ''
                                ? String(draft.value)
                                : ''
                            }
                            onChange={e => {
                              const valueText = e.target.value
                              updateDraft(id, {
                                valueText,
                                value: parseFridgeTempInput(valueText),
                                actionNudged: false,
                              })
                            }}
                            onKeyDown={e => handleTempKeyDown(e, item)}
                            placeholder='°C'
                            aria-label={`${item.definition.name} temperature (°C)`}
                            data-testid={`fridge-temp-${id}`}
                            style={{ width: '7rem' }}
                          />
                          <span>°C</span>
                        </TempField>
                        <Button
                          type={submitAffordance.quiet ? 'default' : 'primary'}
                          loading={submittingId === id}
                          onClick={() => {
                            if (submitAffordance.needsChoice) {
                              updateDraft(id, { actionNudged: true })
                              focusCorrective(id, draft.action)
                              return
                            }
                            submitTemp(item)
                          }}
                          data-testid={`fridge-submit-${id}`}
                          data-chrome={
                            submitAffordance.quiet ? 'quiet' : undefined
                          }
                        >
                          {submitAffordance.label}
                        </Button>
                        {completed ? (
                          <Button
                            onClick={() => {
                              clearDraft(id)
                              setAddingId(null)
                            }}
                            data-testid={`fridge-cancel-add-${id}`}
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </EntryRow>
                      {outOfRangePreview && draftTemp != null ? (
                        <FoodSafetyCorrectiveActionPanel
                          section={SECTION}
                          reading={{
                            value: draftTemp,
                            tempMin: config.tempMin,
                            tempMax: config.tempMax,
                          }}
                          draft={{
                            ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT,
                            ...(draft.action || {}),
                          }}
                          onChange={patch => updateActionDraft(id, patch)}
                          showRecheck={viewingToday}
                          onNoteEnter={() => submitTemp(item)}
                          onSaveWithoutAction={() => submitTemp(item)}
                          testIdPrefix={`fridge-corrective-${id}`}
                        />
                      ) : null}
                    </>
                  ) : null}

                  {completed &&
                  canAdd &&
                  !showEntry &&
                  !paused &&
                  dueSlots.length === 0 ? (
                    <AddAnother
                      onClick={() => {
                        setAddingId(id)
                        focusDefinition(id)
                      }}
                      data-testid={`fridge-add-another-${id}`}
                    >
                      + {FOOD_SAFETY_FRIDGE_ADD_READING_LABEL}
                    </AddAnother>
                  ) : null}

                  {completed && !canAdd && !paused && dueSlots.length === 0 ? (
                    <MaxHint data-testid={`fridge-readings-limit-${id}`}>
                      {formatFridgeReadingsLimitHint()}
                    </MaxHint>
                  ) : null}

                  {canMarkOut ? (
                    <MarkOutLink
                      type='button'
                      onClick={() => outOfService.openMark(def)}
                      data-testid={`fridge-mark-out-of-service-${id}`}
                    >
                      Mark out of service
                    </MarkOutLink>
                  ) : null}
                </UnitCard>
              )
            })}
          </CardList>
        </>
      )}

      {dueTotal > 0 && openCount === 0 ? (
        <AllDone>All fridges and freezers logged for this day.</AllDone>
      ) : null}

      <FoodSafetyOutOfServiceDialog outOfService={outOfService} />

      <FoodSafetyReportsPaywallModal
        visible={historyPaywallOpen}
        reason={FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER}
        olderRecordCount={(day?.historyAccess || lockAccess)?.olderRecordCount}
        upgradeHint={(day?.historyAccess || lockAccess)?.upgradeHint}
        onClose={() => setHistoryPaywallOpen(false)}
      />
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 42rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  ${ollieFabPageClearanceCss}

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
    ${ollieFabPageClearanceCss}
  `};
`

const TopBar = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  font-weight: 600;
`

const EmptyCard = styled(FoodSafetySurfaceCard)`
  border-style: dashed;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const SetupLink = styled(Link)`
  font-weight: 600;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  align-self: flex-start;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const OpenLeftSticky = styled.div`
  position: ${({ $quiet }) => ($quiet ? 'static' : 'sticky')};
  top: ${({ $quiet }) => ($quiet ? 'auto' : 0)};
  z-index: ${({ $quiet }) => ($quiet ? 0 : 5)};
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.35rem 0.75rem;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  padding: ${({ $quiet }) => ($quiet ? '0.2rem 0' : '0.55rem 0.75rem')};
  border: ${({ $quiet }) =>
    $quiet ? 'none' : `1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER}`};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ $quiet }) =>
    $quiet ? 'transparent' : FOOD_SAFETY_SURFACE.HERO_BG};
  border-left: ${({ $quiet, theme }) =>
    $quiet ? 'none' : `3px solid ${theme.colours.lettuceGreen || '#b5d922'}`};
  box-shadow: ${({ $quiet }) =>
    $quiet ? 'none' : FOOD_SAFETY_SURFACE.CARD_SHADOW};
`

const OpenLeftLabel = styled.strong`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colours.bbqBlack || '#132d07'};
`

const OpenLeftHint = styled.span`
  font-size: ${({ $quiet }) => ($quiet ? '0.72rem' : '0.8rem')};
  font-weight: ${({ $quiet }) => ($quiet ? 400 : 600)};
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const UnitCard = styled(FoodSafetySurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};

  &[data-completed='true'] {
    opacity: 0.92;
  }

  &[data-ollie-linked='true'] {
    outline: 2px solid ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    outline-offset: 2px;
  }
`

const UnitHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-start;
`

const UnitName = styled(H3)`
  margin: 0;
  font-size: 1.05rem;
`

const MetaLine = styled.div`
  margin-top: 0.15rem;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const NextBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
`

const DoneBadge = styled(NextBadge)`
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const MissedBadge = styled(NextBadge)`
  color: #c45c26;
`

const LinkedMissedLine = styled.p`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
`

const EntryRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`

const TempField = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;

  &[data-tone='fail'] .ant-input,
  &[data-tone='fail'] > span:last-child {
    color: #c47a12;
    border-color: #c47a12;
  }

  &[data-tone='pass'] .ant-input,
  &[data-tone='pass'] > span:last-child {
    color: #5a9e0b;
  }
`

const SignButton = styled.button`
  min-width: 3rem;
  min-height: 2.75rem;
  padding: 0 0.75rem;
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  color: ${({ theme }) => theme.colours.bbqBlack || '#132d07'};

  &:hover {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  }
`

const LoggedRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-bottom: 0.65rem;

  &[data-separated='true'] {
    margin-top: 0.35rem;
    padding-top: 0.75rem;
    border-top: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  }
`

const LoggedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`

const LoggedTemp = styled.strong`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};

  &[data-tone='pass'] {
    color: #5a9e0b;
  }

  &[data-tone='fail'] {
    color: #c47a12;
  }
`

const OutStatus = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #c47a12;
`

const SlotLabel = styled.div`
  margin-top: 0.65rem;
  font-size: 0.8125rem;
  font-weight: 700;
`

const LoggedMeta = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

/** Logged-day recheck — outline secondary (29.54); not link, not primary. */
const AddAnother = styled(Button)`
  align-self: flex-start;
  margin-top: ${({ theme }) => theme.spacing.xs};
  font-weight: 600;
  border-color: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#3d5c0a'};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
    color: ${({ theme }) => theme.colours.seaweedGreen || '#3d5c0a'};
    background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  }
`

const FiveReadingsNote = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ReadingPosition = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.xs};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const OutOfServiceBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #5c5c5c;
  background: #ececec;
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
  white-space: nowrap;
`

const OutOfServiceNote = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  padding: 0.65rem 0.75rem;
  border-radius: 6px;
  background: #f5f5f5;
  border: 1px dashed #cfcfcf;
`

const OutOfServiceLine = styled.span`
  font-weight: 600;
`

const OutOfServiceHint = styled.span`
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ReturnButton = styled(Button)`
  margin-top: 0.2rem;
  font-weight: 600;
`

/** Quiet — never competes with the reading entry. */
const MarkOutLink = styled.button`
  align-self: flex-end;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;
`

const MaxHint = styled.p`
  margin: ${({ theme }) => theme.spacing.xs} 0 0;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const AllDone = styled.p`
  margin-top: ${({ theme }) => theme.spacing.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
`

export default FoodSafetyFridgesScreen
